<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { DOMSerializer } from '@tiptap/pm/model'
import StarterKit            from '@tiptap/starter-kit'
import Image                 from '@tiptap/extension-image'
import Placeholder           from '@tiptap/extension-placeholder'

const props = defineProps({
    modelValue  : { type: String,  default: '' },
    editable    : { type: Boolean, default: true },
    placeholder : { type: String,  default: 'Your text here...' },
    enableLinks : { type: Boolean, default: false },
    enableImages: { type: Boolean, default: false },
    enableAssistant: { type: Boolean, default: false },
    assistantLabel: { type: String, default: 'AI' },
    height      : { type: String,  default: '200px' }
})
const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'update', 'assistant'])

const editor = ref(null)
const hasFocus = ref(false)
const lastSelection = ref(null)
const assistantButton = ref({ visible: false, top: 0, left: 0 })

const initEditor = () => {
    editor.value = new Editor({
        content     : props.modelValue,
        editable    : props.editable,
        editorProps : {
            handleClick: () => {
                requestAnimationFrame(updateAssistantButton)

                return false
            },
            handleKeyDown: () => {
                requestAnimationFrame(updateAssistantButton)

                return false
            },
        },
        extensions  : [
            StarterKit.configure({
                history: { depth: 100, newGroupDelay: 300 },
                underline: {},
                link: {
                    openOnClick   : false,
                    autolink      : true,
                    protocols     : ['http', 'https', 'mailto', 'tel'],
                    validate      : href => /^((https?:)?\/\/|mailto:|tel:).+/.test(href)
                }
            }),
            Image.configure({
                HTMLAttributes: { class: 'max-w-full h-auto rounded' }
            }),
            Placeholder.configure({
                placeholder   : props.placeholder
            })
        ],
        onUpdate   : ({ editor }) => {
            emit('update:modelValue', editor.getHTML())
            emit('update', editor.getHTML())
        },
        onSelectionUpdate: ({ editor }) => {
            lastSelection.value = {
                from: editor.state.selection.from,
                to: editor.state.selection.to,
            }
            updateAssistantButton()
        },
        onFocus    : () => {
            hasFocus.value = true
            updateAssistantButton()
            emit('focus')
        },
        onBlur     : () => {
            hasFocus.value = false
            lastSelection.value = editor.value
                ? {
                    from: editor.value.state.selection.from,
                    to: editor.value.state.selection.to,
                }
                : null
            setTimeout(() => {
                if (!hasFocus.value) {
                    hideAssistantButton()
                }
            }, 120)
            emit('blur')
        }
    })
}

onMounted(() => {
    initEditor()
    window.addEventListener('resize', hideAssistantButton)
    window.addEventListener('scroll', hideAssistantButton, true)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', hideAssistantButton)
    window.removeEventListener('scroll', hideAssistantButton, true)
    editor.value?.destroy()
})

// Mantieni in sync contenuto esterno → editor (evita loop)
watch(
    () => props.modelValue,
    (html) => {
        const current = editor.value?.getHTML()
        if (editor.value && html !== current) {
            editor.value.commands.setContent(html || '', false)
        }
    }
)

// Abilita/disabilita editing runtime
watch(
    () => props.editable,
    (val) => editor.value?.setEditable(val)
)

// -------- Funzioni barra strumenti --------
const canUndo = computed(() => editor.value?.can().undo() ?? false)
const canRedo = computed(() => editor.value?.can().redo() ?? false)

const setHeading = (level) => {
    editor.value?.chain().focus().toggleHeading({ level }).run()
}

const setLink = async () => {
    const prev = editor.value?.getAttributes('link')?.href ?? ''
    const url  = window.prompt('Inserisci URL', prev)
    if (url === null) return
    if (url === '') {
        editor.value?.chain().focus().unsetLink().run()
        return
    }
    editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

const addImageByUrl = async () => {
    const url = window.prompt('URL immagine')
    if (!url) return
    editor.value?.chain().focus().setImage({ src: url }).run()
}

const clearFormatting = () => {
    editor.value?.chain().focus().unsetAllMarks().clearNodes().run()
}

const getSelectionRange = (selection = null) => {
    const source = selection || editor.value?.state.selection || lastSelection.value
    const from = Number(source?.from)
    const to = Number(source?.to)

    if (!Number.isFinite(from) || !Number.isFinite(to)) {
        return null
    }

    return {
        from: Math.min(from, to),
        to: Math.max(from, to),
    }
}

const getSelectedHtml = (from, to) => {
    if (!editor.value || from === to) {
        return ''
    }

    const slice = editor.value.state.doc.slice(from, to)
    const serializer = DOMSerializer.fromSchema(editor.value.state.schema)
    const container = document.createElement('div')

    container.appendChild(serializer.serializeFragment(slice.content))

    return container.innerHTML
}

const getSelectionSnapshot = () => {
    if (!editor.value) {
        return null
    }

    const range = getSelectionRange()
    const from = range?.from ?? editor.value.state.selection.from
    const to = range?.to ?? editor.value.state.selection.to
    const hasSelection = from !== to

    return {
        fullHtml: editor.value.getHTML(),
        fullText: editor.value.getText(),
        hasSelection,
        selection: { from, to },
        selectionHtml: hasSelection ? getSelectedHtml(from, to) : '',
        selectionText: hasSelection ? editor.value.state.doc.textBetween(from, to, '\n\n') : '',
        anchor: {
            top: assistantButton.value.top,
            left: assistantButton.value.left,
        },
    }
}

const insertContent = (content) => {
    if (!editor.value || !content) {
        return
    }

    if (hasFocus.value) {
        editor.value.chain().focus().insertContent(content).run()
        return
    }

    if (lastSelection.value) {
        editor.value.chain().insertContentAt(lastSelection.value, content).run()
        return
    }

    editor.value.chain().focus('end').insertContent(content).run()
}

const replaceSelection = (content, selection = null) => {
    if (!editor.value || !content) {
        return
    }

    const range = getSelectionRange(selection)

    if (!range) {
        insertContent(content)
        return
    }

    editor.value.chain().focus().insertContentAt(range, content).run()
}

const replaceAll = (content) => {
    if (!editor.value) {
        return
    }

    editor.value.commands.setContent(content || '', false)
    emit('update:modelValue', editor.value.getHTML())
    emit('update', editor.value.getHTML())
}

const insertAtCursor = (content, selection = null) => {
    if (!editor.value || !content) {
        return
    }

    const range = getSelectionRange(selection)

    if (!range) {
        insertContent(content)
        return
    }

    editor.value.chain().focus().insertContentAt({ from: range.from, to: range.from }, content).run()
}

const updateAssistantButton = () => {
    if (!props.enableAssistant || !props.editable || !editor.value || !hasFocus.value) {
        hideAssistantButton()
        return
    }

    const selection = editor.value.state.selection
    const position = selection.empty ? selection.from : selection.to
    const coords = editor.value.view.coordsAtPos(position)

    assistantButton.value = {
        visible: true,
        top: Math.max(12, coords.top - 46),
        left: Math.min(window.innerWidth - 44, Math.max(12, coords.left - 18)),
    }
}

const hideAssistantButton = () => {
    assistantButton.value = {
        ...assistantButton.value,
        visible: false,
    }
}

const triggerAssistant = () => {
    const snapshot = getSelectionSnapshot()

    if (!snapshot) {
        return
    }

    hideAssistantButton()
    emit('assistant', snapshot)
}

defineExpose({
    insertContent,
    getSelectionSnapshot,
    replaceSelection,
    replaceAll,
    insertAtCursor,
    focusEnd: () => editor.value?.chain().focus('end').run(),
})
</script>

<template>
    <div class="w-full min-w-0">
        <!-- Barra strumenti -->
        <div class="flex flex-wrap gap-2 rounded-xl border bg-white p-2 shadow-sm">
            <!-- Formattazione testo -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('bold') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleBold().run()"
                title="Bold"
            >
                <fa-icon icon="bold" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('italic') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleItalic().run()"
                title="Italic"
            >
                <fa-icon icon="italic" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('underline') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleUnderline().run()"
                title="Underline"
            >
                <fa-icon icon="underline" />
            </button>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- Titoli -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('heading', { level: 1 }) ? 'bg-gray-100' : ''"
                @click="setHeading(1)"
                title="Heading 1"
            >
                <fa-icon icon="heading" class="mr-1" />
                <span class="text-xs">1</span>
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('heading', { level: 2 }) ? 'bg-gray-100' : ''"
                @click="setHeading(2)"
                title="Heading 2"
            >
                <fa-icon icon="heading" class="mr-1" />
                <span class="text-xs">2</span>
            </button>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- Liste -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('bulletList') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleBulletList().run()"
                title="Bullet List"
            >
                <fa-icon icon="list-ul" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                :class="editor?.isActive('orderedList') ? 'bg-gray-100' : ''"
                @click="editor?.chain().focus().toggleOrderedList().run()"
                title="Numbered List"
            >
                <fa-icon icon="list-ol" />
            </button>

            <!-- Link e immagini -->
            <template v-if="enableLinks || enableImages">
                <div class="w-px self-stretch bg-gray-200 mx-1" />

                <button
                    type="button"
                    v-if="enableLinks"
                    class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                    :class="editor?.isActive('link') ? 'bg-gray-100' : ''"
                    @click="setLink"
                    title="Insert Link"
                >
                    <fa-icon icon="link" />
                </button>

                <button
                    type="button"
                    v-if="enableImages"
                    class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                    @click="addImageByUrl"
                    title="Insert Image"
                >
                    <fa-icon icon="image" />
                </button>
            </template>

            <div class="w-px self-stretch bg-gray-200 mx-1" />

            <!-- Cronologia e pulizia -->
            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="!canUndo"
                @click="editor?.chain().focus().undo().run()"
                title="Undo"
            >
                <fa-icon icon="rotate-left" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                :disabled="!canRedo"
                @click="editor?.chain().focus().redo().run()"
                title="Redo"
            >
                <fa-icon icon="rotate-right" />
            </button>

            <button
                type="button"
                class="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
                @click="clearFormatting"
                title="Clear Formatting"
            >
                <fa-icon icon="eraser" />
            </button>
        </div>

        <!-- Area editor -->
        <div
            class="cm-input-borders mt-2 max-w-full overflow-y-auto bg-white p-3 prose max-w-none"
            :style="{ height: height }"
        >
            <EditorContent :editor="editor" />
        </div>
    </div>

    <Teleport to="body">
        <button
            v-if="enableAssistant && assistantButton.visible"
            type="button"
            class="fixed z-[100001] flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition hover:bg-indigo-700"
            :style="{ top: `${assistantButton.top}px`, left: `${assistantButton.left}px` }"
            :title="assistantLabel"
            :aria-label="assistantLabel"
            @mousedown.prevent.stop
            @click.prevent.stop="triggerAssistant"
        >
            <fa-icon icon="wand-magic-sparkles" />
        </button>
    </Teleport>
</template>

<style scoped>
.prose :deep(p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    color: #9ca3af; /* gray-400 */
    pointer-events: none;
}

/* Rimuovi outline blu al focus */
.prose :deep(.ProseMirror) {
    max-width: 100%;
    outline: none;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.prose :deep(.ProseMirror *) {
    max-width: 100%;
}

.prose :deep(.ProseMirror blockquote) {
    margin: 1rem 0 0;
    border-left: 3px solid #cbd5e1;
    padding-left: 1rem;
    color: #64748b;
}

.prose :deep(.ProseMirror blockquote p) {
    margin: 0.35rem 0;
}

.prose :deep(.ProseMirror:focus) {
    outline: none;
}
</style>
